/**
 * Cours particuliers (booking) — accès tuteur. Tables/RPC postérieures aux
 * types générés (`types.generated.ts` n'est jamais régénéré, cf. CLAUDE.md) →
 * client générique (calque `apps/app/src/lib/live-campaigns.ts`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { RendezVousKind, SchoolLevel } from '@jeraisonne/db';
import { isRendezVousUpcoming, toEndedAtMs } from '@jeraisonne/db/visio';
import { supabase } from '@/lib/supabase';
import { fetchArchivedReservationIds } from '@/lib/rendezvous-archive';

const db = supabase as unknown as SupabaseClient;

// ── Types ──────────────────────────────────────────────────────────────────────

export interface TutorBookingRule {
  id: string;
  tutor_id: string;
  weekday: number; // 0=dimanche..6=samedi
  start_time: string; // 'HH:MM:SS' (heure locale Europe/Paris)
  duration_minutes: number;
  level: SchoolLevel | null;
  chapter_id: string | null;
  chapter_title?: string | null;
  capacity: number;
  price_cents: number;
  is_active: boolean;
  /** cours/etude uniquement — une règle récurrente ne porte jamais express/entretien. */
  kind: RendezVousKind;
  created_at: string;
}

export type TutorBookingSlotStatus = 'open' | 'cancelling' | 'cancelled';

export interface TutorBookingSlot {
  id: string;
  rule_id: string | null;
  tutor_id: string;
  starts_at: string;
  ends_at: string;
  level: SchoolLevel | null;
  chapter_id: string | null;
  capacity: number;
  price_cents: number;
  status: TutorBookingSlotStatus;
  kind: RendezVousKind;
}

export type TutorBookingReservationStatus =
  | 'pending_payment'
  | 'confirmed'
  | 'cancelled'
  | 'expired'
  | 'refunded';

export interface TutorBookingReservation {
  id: string;
  slot_id: string;
  student_id: string | null;
  guest_email: string | null;
  guest_name: string | null;
  status: TutorBookingReservationStatus;
  price_cents: number;
  payment_deadline: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  /** Fin RÉELLE (le tuteur a terminé la réunion, migration 0249) : le rendez-vous est alors passé immédiatement. */
  ended_at?: string | null;
  created_at: string;
  student_display_name?: string | null;
}

export interface TutorBookingSlotWithReservations extends TutorBookingSlot {
  reservations: TutorBookingReservation[];
}

// ── Statut Stripe Connect + toggle booking_enabled ────────────────────────────

export interface TutorBookingStatus {
  booking_enabled: boolean;
  stripe_connect_charges_enabled: boolean;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_onboarded_at: string | null;
  is_admin: boolean;
}

export function useTutorBookingStatus(tutorId: string | undefined, isAdmin: boolean) {
  return useQuery({
    queryKey: ['tutor-booking-status', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorBookingStatus> => {
      const { data, error } = await db
        .from('tutor_profiles')
        .select(
          'booking_enabled, stripe_connect_charges_enabled, stripe_connect_payouts_enabled, stripe_connect_onboarded_at',
        )
        .eq('id', tutorId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return {
        booking_enabled: Boolean(data?.booking_enabled),
        stripe_connect_charges_enabled: Boolean(data?.stripe_connect_charges_enabled),
        stripe_connect_payouts_enabled: Boolean(data?.stripe_connect_payouts_enabled),
        stripe_connect_onboarded_at: data?.stripe_connect_onboarded_at ?? null,
        is_admin: isAdmin,
      };
    },
  });
}

export function useSetTutorBookingEnabled(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!tutorId) throw new Error('missing tutorId');
      const { error } = await db
        .from('tutor_profiles')
        .update({ booking_enabled: enabled })
        .eq('id', tutorId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tutor-booking-status', tutorId] }),
  });
}

/** Lance l'onboarding Stripe Connect Express, renvoie l'URL hébergée à ouvrir. */
export function useTutorConnectOnboard() {
  return useMutation({
    mutationFn: async (returnTo: string): Promise<{ url: string }> => {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'tutor-connect-onboard',
        {
          body: { return_to: returnTo },
        },
      );
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('onboarding_url_missing');
      return data;
    },
  });
}

// ── Profil de cours (réglage global : niveau/chapitre/capacité/prix) ─────────
//
// Un seul profil par tuteur (stocké sur tutor_profiles.booking_default_*,
// migration 0219), appliqué à TOUTES ses règles — la grille hebdomadaire ne
// gère que le planning (jour × heure), pas ces attributs par case.

export interface TutorBookingDefaults {
  level: SchoolLevel | null;
  chapter_id: string | null;
  chapter_title: string | null;
  capacity: number;
  price_cents: number | null;
}

export function useTutorBookingDefaults(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-booking-defaults', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorBookingDefaults> => {
      const { data, error } = await db
        .from('tutor_profiles')
        .select(
          'booking_default_level, booking_default_chapter_id, booking_default_capacity, booking_default_price_cents, chapters:booking_default_chapter_id(title)',
        )
        .eq('id', tutorId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const row = data as Record<string, unknown> | null;
      return {
        level: (row?.booking_default_level as SchoolLevel | null) ?? null,
        chapter_id: (row?.booking_default_chapter_id as string | null) ?? null,
        chapter_title: (row?.chapters as { title: string } | null)?.title ?? null,
        capacity: (row?.booking_default_capacity as number | null) ?? 1,
        price_cents: (row?.booking_default_price_cents as number | null) ?? null,
      };
    },
  });
}

export interface TutorBookingDefaultsInput {
  level: SchoolLevel;
  chapter_id: string | null;
  capacity: number;
  price_cents: number;
  /** cours = payant (Stripe) ; etude = gratuit. Bulk-appliqué à toutes les règles du tuteur. */
  kind: RendezVousKind;
}

/** Sauvegarde le profil ET réécrit toutes les règles existantes pour rester cohérent. */
export function useSaveTutorBookingDefaults(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TutorBookingDefaultsInput) => {
      if (!tutorId) throw new Error('missing tutorId');
      // cours/etude : prix au choix du tuteur (etude peut être 0€ = gratuite,
      // ou payante comme un cours, migration 0243). express/entretien restent
      // gratuits (hors périmètre de ce formulaire, gérés par create_direct_rendezvous).
      const priceCents = input.kind === 'cours' || input.kind === 'etude' ? input.price_cents : 0;
      const { error: profileErr } = await db
        .from('tutor_profiles')
        .update({
          booking_default_level: input.level,
          booking_default_chapter_id: input.chapter_id,
          booking_default_capacity: input.capacity,
          booking_default_price_cents: priceCents,
        })
        .eq('id', tutorId);
      if (profileErr) throw new Error(profileErr.message);

      const { error: rulesErr } = await db
        .from('tutor_booking_rules')
        .update({
          level: input.level,
          chapter_id: input.chapter_id,
          capacity: input.capacity,
          price_cents: priceCents,
          kind: input.kind,
        })
        .eq('tutor_id', tutorId);
      if (rulesErr) throw new Error(rulesErr.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tutor-booking-defaults', tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-booking-rules', tutorId] });
    },
  });
}

// ── Règles hebdomadaires (grille jour × heure) ────────────────────────────────

export function useTutorBookingRules(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-booking-rules', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorBookingRule[]> => {
      const { data, error } = await db
        .from('tutor_booking_rules')
        .select('*, chapters(title)')
        .eq('tutor_id', tutorId!)
        .order('weekday')
        .order('start_time');
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: Record<string, unknown>) => ({
        ...row,
        chapter_title: (row.chapters as { title: string } | null)?.title ?? null,
      })) as TutorBookingRule[];
    },
  });
}

export interface TutorBookingRuleInput {
  tutor_id: string;
  weekday: number;
  start_time: string;
  duration_minutes: number;
  level: SchoolLevel;
  chapter_id: string | null;
  capacity: number;
  price_cents: number;
  kind: RendezVousKind;
}

/** Crée une règle pour une case (jour × heure) encore jamais cochée. */
export function useCreateTutorBookingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TutorBookingRuleInput) => {
      const { error } = await db.from('tutor_booking_rules').insert(input);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-booking-rules', variables.tutor_id] });
    },
  });
}

/** Édite une règle existante (is_active pour cocher/décocher, duration_minutes pour la durée). */
export function useUpdateTutorBookingRule(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ruleId,
      patch,
    }: {
      ruleId: string;
      patch: Partial<Pick<TutorBookingRule, 'is_active' | 'duration_minutes'>>;
    }) => {
      const { error } = await db.from('tutor_booking_rules').update(patch).eq('id', ruleId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tutor-booking-rules', tutorId] }),
  });
}

// ── Créneaux matérialisés + réservations (dashboard) ──────────────────────────

/** Créneaux à venir du tuteur avec leurs réservations, triés par date. */
export function useTutorBookingSlots(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-booking-slots', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorBookingSlotWithReservations[]> => {
      const { data, error } = await db
        .from('tutor_booking_slots')
        .select(
          '*, tutor_booking_reservations(*, profiles!tutor_booking_reservations_student_id_fkey(display_name:visible_name))',
        )
        .eq('tutor_id', tutorId!)
        .gte('starts_at', new Date(Date.now() - 24 * 3_600_000).toISOString())
        .order('starts_at');
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: Record<string, unknown>) => {
        const { tutor_booking_reservations, ...slot } = row;
        return {
          ...slot,
          reservations: ((tutor_booking_reservations as Record<string, unknown>[]) ?? []).map(
            (r) => ({
              ...r,
              student_display_name:
                (r.profiles as { display_name: string } | null)?.display_name ?? null,
            }),
          ),
        };
      }) as TutorBookingSlotWithReservations[];
    },
  });
}

/** Annule un créneau (2 phases côté serveur : RPC + remboursements Stripe + RPC). */
export function useCancelTutorBookingSlot(tutorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slotId: string) => {
      const { data, error } = await supabase.functions.invoke<{
        refunded: number;
        refund_failed: number;
      }>('tutor-booking-cancel-slot', { body: { slot_id: slotId } });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tutor-booking-slots', tutorId] }),
  });
}

type TutorReservationRow = Record<string, unknown> & {
  profiles: { display_name: string } | null;
  tutor_booking_slots: TutorBookingSlot | null;
};

/**
 * Historique COMPLET (tous élèves confondus), sans borne de date — contrairement
 * à `useTutorBookingSlots` (fenêtre glissante `starts_at >= now - 24h`, pensée
 * pour "à venir"/dashboard), qui rendait TOUT rendez-vous passé de plus de 24h
 * invisible pour le tuteur, PARTOUT (dashboard ET page rendez-vous), alors que
 * la réservation restait bien visible côté élève (`useMyPastBookings`, aucune
 * borne de date). Alimente l'onglet "Passés" de `/tutor/rendez-vous`.
 */
export function useTutorPastBookings(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-past-bookings', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorBookingRow[]> => {
      const [{ data, error }, archivedIds] = await Promise.all([
        db
          .from('tutor_booking_reservations')
          .select(
            '*, profiles!tutor_booking_reservations_student_id_fkey(display_name:visible_name), tutor_booking_slots!inner(*)',
          )
          .eq('tutor_booking_slots.tutor_id', tutorId!)
          .order('created_at', { ascending: false })
          .limit(200),
        fetchArchivedReservationIds(tutorId!),
      ]);
      if (error) throw new Error(error.message);

      const now = Date.now();
      return ((data ?? []) as TutorReservationRow[])
        .filter((row): row is TutorReservationRow & { tutor_booking_slots: TutorBookingSlot } =>
          Boolean(row.tutor_booking_slots),
        )
        .map((row) => {
          const { tutor_booking_slots, profiles, ...reservation } = row;
          return {
            reservation: {
              ...(reservation as unknown as TutorBookingReservation),
              student_display_name: profiles?.display_name ?? null,
            },
            slot: tutor_booking_slots,
          };
        })
        .filter((row) => {
          const archived = archivedIds.has(row.reservation.id);
          const stillUpcoming =
            row.reservation.status === 'confirmed' &&
            isRendezVousUpcoming(
              now,
              new Date(row.slot.ends_at).getTime(),
              toEndedAtMs(row.reservation.ended_at),
            );
          return (
            row.reservation.status === 'cancelled' ||
            row.reservation.status === 'expired' ||
            row.reservation.status === 'refunded' ||
            archived ||
            (row.reservation.status === 'confirmed' && !stillUpcoming)
          );
        })
        .sort((a, b) => b.slot.starts_at.localeCompare(a.slot.starts_at));
    },
  });
}

export interface TutorPastBookingForStudent {
  reservationId: string;
  startsAt: string;
  kind: RendezVousKind;
  status: string;
  archivedByMe: boolean;
}

type TutorReservationForStudentRow = {
  id: string;
  status: string;
  ended_at: string | null;
  tutor_booking_slots: {
    starts_at: string;
    ends_at: string;
    kind: RendezVousKind;
  } | null;
};

/**
 * "Rendez-vous passés" avec UN élève donné, vu par le tuteur — contrairement à
 * `useTutorBookingSlots` (fenêtre glissante des dernières 24h, pensée pour le
 * dashboard), interroge directement `tutor_booking_reservations` sans borne de
 * date : nécessaire pour retrouver notes/transcription/résumé d'un rendez-vous
 * ancien depuis la fiche élève. Calque `useMyPastBookings` (côté élève), avec
 * la double garde `student_id`/`tutor_booking_slots.tutor_id` pour ne jamais
 * fuiter les rendez-vous de cet élève avec un AUTRE tuteur.
 */
export function useTutorPastBookingsForStudent(
  tutorId: string | undefined,
  studentId: string | undefined,
) {
  return useQuery({
    queryKey: ['tutor-past-bookings-for-student', tutorId, studentId],
    enabled: !!tutorId && !!studentId,
    queryFn: async (): Promise<TutorPastBookingForStudent[]> => {
      const [{ data, error }, archivedIds] = await Promise.all([
        db
          .from('tutor_booking_reservations')
          .select(
            'id, status, ended_at, tutor_booking_slots!inner(starts_at, ends_at, kind, tutor_id)',
          )
          .eq('student_id', studentId!)
          .eq('tutor_booking_slots.tutor_id', tutorId!)
          .order('created_at', { ascending: false })
          .limit(100),
        fetchArchivedReservationIds(tutorId!),
      ]);
      if (error) throw new Error(error.message);

      const now = Date.now();
      return ((data ?? []) as unknown as TutorReservationForStudentRow[])
        .filter(
          (
            row,
          ): row is TutorReservationForStudentRow & {
            tutor_booking_slots: NonNullable<TutorReservationForStudentRow['tutor_booking_slots']>;
          } => Boolean(row.tutor_booking_slots),
        )
        .filter((row) => {
          const archived = archivedIds.has(row.id);
          const stillUpcoming =
            row.status === 'confirmed' &&
            isRendezVousUpcoming(
              now,
              new Date(row.tutor_booking_slots.ends_at).getTime(),
              toEndedAtMs(row.ended_at),
            );
          return archived || !stillUpcoming;
        })
        .map((row) => ({
          reservationId: row.id,
          startsAt: row.tutor_booking_slots.starts_at,
          kind: row.tutor_booking_slots.kind,
          status: row.status,
          archivedByMe: archivedIds.has(row.id),
        }))
        .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
    },
  });
}

// ── Bucketing pur (page /tutor/rendez-vous : à venir / paiement / historique) ──

export interface TutorBookingRow {
  reservation: TutorBookingReservation;
  slot: TutorBookingSlot;
}

/**
 * Répartit les réservations d'un tuteur en 3 groupes pour la page
 * `/tutor/rendez-vous`. Un rendez-vous "à venir, confirmé" quitte ce groupe
 * dès qu'il n'est plus joignable (`isRendezVousUpcoming`, fenêtre -10min/+15min)
 * OU dès qu'il a été archivé manuellement par le tuteur (`archivedIds`,
 * migration 0240) — les deux cas basculent la ligne en "Historique", jamais
 * une suppression réelle de la réservation.
 */
export function bucketTutorBookingRows(
  rows: TutorBookingRow[],
  nowMs: number,
  archivedIds: Set<string> | undefined,
): {
  upcomingConfirmed: TutorBookingRow[];
  pendingPayment: TutorBookingRow[];
  history: TutorBookingRow[];
} {
  const upcomingConfirmed = rows
    .filter(
      (r) =>
        r.reservation.status === 'confirmed' &&
        isRendezVousUpcoming(
          nowMs,
          new Date(r.slot.ends_at).getTime(),
          toEndedAtMs(r.reservation.ended_at),
        ) &&
        !archivedIds?.has(r.reservation.id),
    )
    .sort((a, b) => a.slot.starts_at.localeCompare(b.slot.starts_at));

  const pendingPayment = rows
    .filter((r) => r.reservation.status === 'pending_payment')
    .sort((a, b) => a.slot.starts_at.localeCompare(b.slot.starts_at));

  const history = rows
    .filter(
      (r) =>
        r.reservation.status === 'cancelled' ||
        r.reservation.status === 'expired' ||
        r.reservation.status === 'refunded' ||
        (r.reservation.status === 'confirmed' &&
          (!isRendezVousUpcoming(
            nowMs,
            new Date(r.slot.ends_at).getTime(),
            toEndedAtMs(r.reservation.ended_at),
          ) ||
            !!archivedIds?.has(r.reservation.id))),
    )
    .sort((a, b) => b.slot.starts_at.localeCompare(a.slot.starts_at))
    .slice(0, 20);

  return { upcomingConfirmed, pendingPayment, history };
}
